import { jsxs, jsx } from 'react/jsx-runtime';
import { useInput } from '@mui/base';
import { styled, IconButton as IconButton$1 } from '@mui/material';
import { unstable_useForkRef } from '@mui/utils';
import { getBorderWidth, getBorderRadius, getPadding } from '@volt_developers/foundations';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { getRGBA } from '../../utils/getRGBA/getRGBA.js';
import { shouldForwardPropHelper } from '../../utils/shouldForwardHelper.js';
import '../Icons/Add.js';
import '../Icons/Application.js';
import '../Icons/AppStore.js';
import '../Icons/Arrow.js';
import '../Icons/At.js';
import '../Icons/Attach.js';
import '../Icons/Bank.js';
import '../Icons/Battery.js';
import '../Icons/Bell.js';
import '../Icons/Blocked.js';
import '../Icons/Calendar.js';
import '../Icons/Card.js';
import '../Icons/CaretDown.js';
import '../Icons/CaretLeft.js';
import '../Icons/CaretRight.js';
import '../Icons/CaretUp.js';
import '../Icons/Checkbox.js';
import '../Icons/CheckboxChecked.js';
import '../Icons/CheckboxIndeterminate.js';
import '../Icons/ChevronLeft.js';
import '../Icons/ChevronRight.js';
import '../Icons/Company.js';
import '../Icons/Copy.js';
import '../Icons/Currency.js';
import '../Icons/Customers.js';
import '../Icons/Dash.js';
import '../Icons/Dashboard.js';
import '../Icons/DirectionDown.js';
import '../Icons/DirectionUp.js';
import '../Icons/Document.js';
import '../Icons/DoubleChevronLeft.js';
import '../Icons/DoubleChevronRight.js';
import '../Icons/DoubleDirectionDown.js';
import '../Icons/DoubleDirectionUp.js';
import '../Icons/Download.js';
import '../Icons/Edit.js';
import '../Icons/ExclamationMark.js';
import '../Icons/ExclamationMarkFilled.js';
import '../Icons/ExternalLink.js';
import '../Icons/EyeClosed.js';
import '../Icons/EyeOpen.js';
import '../Icons/Fingerprint.js';
import '../Icons/Folder.js';
import '../Icons/Forever.js';
import '../Icons/Funnel.js';
import '../Icons/GooglePlay.js';
import '../Icons/Iban.js';
import '../Icons/Id.js';
import '../Icons/Info.js';
import '../Icons/InfoFilled.js';
import '../Icons/Instant.js';
import '../Icons/Ip.js';
import '../Icons/Link.js';
import '../Icons/List.js';
import '../Icons/Mail.js';
import '../Icons/Menu.js';
import '../Icons/Message.js';
import '../Icons/MessageDot.js';
import '../Icons/Onboarding.js';
import '../Icons/Padlock.js';
import '../Icons/PadlockOpen.js';
import '../Icons/Percent.js';
import '../Icons/Play.js';
import '../Icons/PowerOff.js';
import '../Icons/ProcessCompleted.js';
import '../Icons/ProcessCountEight.js';
import '../Icons/ProcessCountFive.js';
import '../Icons/ProcessCountFour.js';
import '../Icons/ProcessCountNineAndMore.js';
import '../Icons/ProcessCountOne.js';
import '../Icons/ProcessCountSeven.js';
import '../Icons/ProcessCountSix.js';
import '../Icons/ProcessCountThree.js';
import '../Icons/ProcessCountTwo.js';
import '../Icons/ProcessFailed.js';
import '../Icons/ProcessInProgress.js';
import '../Icons/ProcessNotStarted.js';
import '../Icons/ProcessPartialInProgress.js';
import '../Icons/ProcessPaused.js';
import '../Icons/Profile.js';
import '../Icons/Progress.js';
import '../Icons/Provider.js';
import '../Icons/QuestionMark.js';
import '../Icons/QuestionMarkFilled.js';
import Remove from '../Icons/Remove.js';
import '../Icons/Repeat.js';
import '../Icons/Reset.js';
import '../Icons/RoundArrow.js';
import '../Icons/RoundArrowFilled.js';
import '../Icons/RoundCheckOutlined.js';
import '../Icons/RoundCheckOutlinedFilled.js';
import '../Icons/RoundClose.js';
import '../Icons/RoundCloseFilled.js';
import '../Icons/RoundLeft.js';
import '../Icons/RoundLeftFilled.js';
import '../Icons/RoundMinus.js';
import '../Icons/RoundMinusFilled.js';
import '../Icons/RoundPlus.js';
import '../Icons/RoundPlusFilled.js';
import '../Icons/RoundRight.js';
import '../Icons/RoundRightFilled.js';
import '../Icons/Settings.js';
import '../Icons/SettingsCog.js';
import '../Icons/Shield.js';
import '../Icons/StarFilled.js';
import '../Icons/StarOutlined.js';
import '../Icons/Success.js';
import '../Icons/Timer.js';
import '../Icons/Trash.js';
import '../Icons/Upload.js';
import '../Icons/User.js';
import '../Icons/VerticalDots.js';
import '../Icons/World.js';
import '../Icons/Wrench.js';
import '../Icons/Zoom.js';
import { INPUT_COLORS, INPUT_HEIGHT_LARGE, INPUT_HEIGHT_MEDIUM } from './styles.js';

const PROPS_SET = new Set([
    "size",
    "startAdornment",
    "endAdornment",
    "error",
    "onClear",
    "onSearch",
]);
const Input = forwardRef(function InputComponent(props, ref) {
    const { getRootProps, getInputProps } = useInput(props);
    const { className, value, error, readOnly, disabled, size, onClear, startAdornment, endAdornment, sx, } = props;
    const inputProps = getInputProps();
    inputProps.ref = unstable_useForkRef(inputProps.ref, ref);
    const isClearVisible = () => {
        var _a;
        if (!onClear) {
            return false;
        }
        if (value) {
            return Boolean(value);
        }
        if (ref !== null && typeof ref !== "function") {
            return Boolean((_a = ref.current) === null || _a === void 0 ? void 0 : _a.value);
        }
        return false;
    };
    return (jsxs(InputRoot, Object.assign({ className: clsx("VoltInput-root", className), disabled: disabled, error: error, readOnly: readOnly, size: size, sx: sx, onClear: onClear }, getRootProps(), { children: [startAdornment && jsx(InputAdornment, { size: size, children: startAdornment }), jsx(InputInput, Object.assign({}, props, inputProps, { endAdornment: Boolean(endAdornment), startAdornment: Boolean(startAdornment) })), isClearVisible() && (jsx(ClearButton, { onClick: onClear, children: jsx(Remove, { fontSize: "small" }) })), endAdornment && jsx(InputAdornment, { size: size, children: endAdornment })] })));
});
const INPUT_ROOT_PROPS_SET = new Set([
    "disabled",
    "readOnly",
    "error",
    "onClear",
    "onSearch",
    "size",
]);
const InputRoot = styled("div", {
    name: "VoltInput",
    slot: "Root",
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, INPUT_ROOT_PROPS_SET),
})(({ error, disabled, readOnly, size }) => (Object.assign(Object.assign(Object.assign(Object.assign({ overflow: "hidden", background: INPUT_COLORS.container.background.default, borderWidth: getBorderWidth(1), borderStyle: "solid", borderColor: INPUT_COLORS.container.border.default, borderRadius: getBorderRadius(1), color: INPUT_COLORS.container.body.active, alignItems: "center", display: "flex", flexWrap: "nowrap", width: "100%", transition: `
    background-color 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border-color 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    color 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    outline 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms
  `, height: size === "large" ? INPUT_HEIGHT_LARGE : INPUT_HEIGHT_MEDIUM, "&:hover": {
        boxShadow: `
      0px 4px 4px ${getRGBA("#20252B", 0.05)}
    `,
    }, [`& ${InputAdornment}, & ${IconButton}`]: Object.assign({ color: INPUT_COLORS.container.border.active }, (disabled && {
        color: INPUT_COLORS.container.body.default,
    })), [`& ${ClearButton}`]: {
        color: INPUT_COLORS.container.body.default,
    } }, (readOnly && {
    pointerEvents: "none",
    "&:hover": {
        boxShadow: "none",
    },
    "&:focus": {
        boxShadow: "none",
    },
})), (disabled && {
    background: INPUT_COLORS.container.background.disabled,
    color: INPUT_COLORS.container.body.default,
    pointerEvents: "none",
    "&:hover": {
        boxShadow: "none",
    },
})), { "&:focus-within": Object.assign({ outline: 0, borderColor: INPUT_COLORS.container.border.active, boxShadow: `
      0px 2px 2px ${getRGBA("#1A00AB", 0.15)},
      0px 4px 8px ${getRGBA("#20252B", 0.15)}
    ` }, (error && {
        borderColor: INPUT_COLORS.container.border.error,
        boxShadow: `
        0px 2px 2px ${getRGBA("#DB1C1C", 0.2)},
        0px 4px 8px ${getRGBA("#20252B", 0.2)}
      `,
    })) }), (error && {
    borderColor: INPUT_COLORS.container.border.error,
}))));
const InputAdornment = styled("div", {
    name: "VoltInput",
    slot: "Adornment",
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, PROPS_SET),
})(({ theme, size }) => (Object.assign(Object.assign(Object.assign({ display: "flex", alignSelf: "stretch" }, theme.typography.small), { "& .MuiSvgIcon-root": {
        fontSize: 16,
    } }), (size === "large" && {
    "& .MuiSvgIcon-root": {
        fontSize: 18,
    },
}))));
const InputInput = styled("input", {
    name: "VoltInput",
    slot: "Input",
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, PROPS_SET),
})(({ theme, size, startAdornment, endAdornment }) => [
    Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ flex: "1 1 100%", alignSelf: "flex-start", background: "transparent", border: 0, color: INPUT_COLORS.container.body.active, cursor: "text", padding: getPadding(size === "large" ? 4 : 3) }, theme.typography.small), { "&:disabled, &:disabled:read-only": {
            color: INPUT_COLORS.container.body.default,
        }, "&::placeholder": {
            color: INPUT_COLORS.container.body.default,
        }, "&:read-only": {
            color: INPUT_COLORS.container.body.active,
        }, "&:focus": {
            outline: 0,
        } }), (size === "large" && Object.assign({}, theme.typography.normal))), (Boolean(startAdornment) && {
        paddingLeft: 0,
    })), (Boolean(endAdornment) && {
        paddingRight: 0,
    })),
]);
const BUTTON_PROPS_SET = new Set(["active", "error"]);
const IconButton = styled(IconButton$1, {
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, BUTTON_PROPS_SET),
})(({ active = false, size = "medium" }) => (Object.assign({ padding: getPadding(size === "large" ? 4 : 3), borderRadius: 0, "&:hover": Object.assign({ background: "transparent" }, (active && {
        background: INPUT_COLORS.container.border.active,
    })), "& .MuiSvgIcon-colorHigh": {
        color: INPUT_COLORS.container.body.active,
    } }, (active && {
    background: INPUT_COLORS.container.border.active,
    "& .MuiSvgIcon-root": {
        color: INPUT_COLORS.container.background.default,
    },
}))));
const ClearButton = styled(IconButton)({
    color: INPUT_COLORS.container.body.default,
    "&:hover": {
        backgroundColor: "transparent",
    },
});

export { IconButton, Input };
//# sourceMappingURL=Input.js.map
